import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

const side = (over = {}) => ({
  logo: null,
  logoPlacement: { x: 50, y: 42, scale: 26 },
  text: "",
  textPlacement: { x: 50, y: 74, size: 7 },
  textColor: "#111111",
  ...over,
});
const DEFAULT_CUSTOM = () => ({ front: side(), back: side({ logoPlacement: { x: 50, y: 40, scale: 26 } }) });

const load = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => load("ms_cart", []));
  const [customization, setCustomizationState] = useState(() => {
    const c = load("ms_custom", null);
    return c && c.front && c.back ? c : DEFAULT_CUSTOM();
  });

  useEffect(() => localStorage.setItem("ms_cart", JSON.stringify(items)), [items]);
  useEffect(() => localStorage.setItem("ms_custom", JSON.stringify(customization)), [customization]);

  const lineKey = (slug, color) => `${slug}|${color || ""}`;

  const addItem = (slug, quantity, color) => {
    const key = lineKey(slug, color);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      return [...prev, { key, slug, quantity, color: color || null }];
    });
  };

  const setQuantity = (key, quantity) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, quantity) } : i)));

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const setCustomization = (c) => setCustomizationState(c);
  const updateSide = (s, patch) => setCustomizationState((c) => ({ ...c, [s]: { ...c[s], ...patch } }));

  const clear = () => {
    setItems([]);
    setCustomizationState(DEFAULT_CUSTOM());
  };

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const hasCustom = ["front", "back"].some((s) => customization[s].logo || customization[s].text);

  return (
    <CartContext.Provider
      value={{ items, addItem, setQuantity, removeItem, clear, count, customization, setCustomization, updateSide, hasCustom }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
